
import React, { useState, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import { ReportPermissionLevel, MaintenanceRequest, MaintenanceStatus } from '../../types';
import { safeFormat } from '../../utils/safeDateFns';

interface MaintenanceReportProps {
    requests?: MaintenanceRequest[];
    permissionLevel?: ReportPermissionLevel;
    initialLevel?: 'form' | 'preview' | 'history';
    onLevelChange?: (level: 'form' | 'preview' | 'history') => void;
}

const MaintenanceReport: React.FC<MaintenanceReportProps> = ({ 
    requests = [],
    permissionLevel = 'view',
    initialLevel = 'form',
    onLevelChange
}) => {
    const canExport = permissionLevel === 'export';

    const [currentLevel, setCurrentLevel] = useState<'form' | 'preview' | 'history'>(initialLevel);
    const [selectedStatus, setSelectedStatus] = useState<MaintenanceStatus | 'All'>('All');
    const [startDate, setStartDate] = useState(safeFormat(new Date(Date.now() - 30 * 86400000), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(safeFormat(new Date(), 'yyyy-MM-dd'));

    const handleLevelChange = (level: 'form' | 'preview' | 'history') => {
        setCurrentLevel(level);
        if (onLevelChange) onLevelChange(level);
    };

    const filteredRequests = useMemo(() => {
        return requests.filter(req => {
            const reqDate = new Date(req.createdAt).getTime();
            const selStart = new Date(startDate).getTime();
            const selEnd = new Date(endDate).getTime();
            const dateMatch = reqDate >= selStart && reqDate <= selEnd;
            const statusMatch = selectedStatus === 'All' || req.status === selectedStatus;
            return dateMatch && statusMatch;
        });
    }, [requests, startDate, endDate, selectedStatus]);

    const renderForm = () => (
        <div className="max-w-xl mx-auto">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-slate-800 space-y-8">
                <div className="flex items-center gap-4 mb-2">
                    <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center text-indigo-600">
                        <Icons.Tool className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Maintenance Filters</h3>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Generate Repair Summary</p>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Start Date</label>
                            <input 
                                type="date" 
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">End Date</label>
                            <input 
                                type="date" 
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1">Status Filter</label>
                        <select 
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value as any)}
                            className="w-full bg-gray-50 dark:bg-slate-800 border-none rounded-2xl p-4 font-bold text-sm focus:ring-2 focus:ring-indigo-500 transition-all"
                        >
                            <option value="All">All Statuses</option>
                            <option value="Open">Open</option>
                            <option value="In Progress">In Progress</option>
                            <option value="On Hold">On Hold</option>
                            <option value="Completed">Completed</option>
                            <option value="Cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>

                <button 
                    onClick={() => handleLevelChange('preview')}
                    className="w-full bg-indigo-600 text-white py-6 rounded-[32px] font-black uppercase tracking-widest shadow-2xl shadow-indigo-200 dark:shadow-none hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 group"
                >
                    Generate Report
                    <Icons.ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
    );

    const renderPreview = () => (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-[32px] border border-gray-100 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 no-print">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => handleLevelChange('form')}
                        className="w-10 h-10 rounded-full bg-gray-50 dark:bg-slate-800 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                        <Icons.ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-tight">Maintenance Report</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{selectedStatus} Requests • {startDate} — {endDate}</p>
                    </div>
                </div>

                {canExport && (
                    <button 
                        onClick={() => window.print()}
                        className="px-6 py-2 bg-gray-900 text-white rounded-xl text-xs font-bold hover:opacity-90 transition-all flex items-center gap-2"
                    >
                        <Icons.Printer className="w-4 h-4" /> Print Report
                    </button>
                )}
            </div>

            <div className="bg-white p-8 rounded-[40px] shadow-sm border border-gray-200 overflow-x-auto">
                <table className="w-full text-left text-[11px] border-collapse">
                    <thead>
                        <tr className="bg-gray-50 uppercase tracking-widest text-gray-500 font-black border-b border-gray-100">
                            <th className="px-4 py-4">ID / Type</th>
                            <th className="px-4 py-4">Location</th>
                            <th className="px-4 py-4">Title / Description</th>
                            <th className="px-4 py-4">Priority</th>
                            <th className="px-4 py-4">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {filteredRequests.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-12 text-center text-gray-400 font-bold uppercase tracking-widest">No requests found for this period</td>
                            </tr>
                        ) : (
                            filteredRequests.map(req => (
                                <tr key={req.id}>
                                    <td className="px-4 py-4">
                                        <span className="font-bold text-indigo-600">{req.requestId}</span> <br/>
                                        <span className="text-[9px] uppercase font-bold text-gray-400">{req.type}</span>
                                    </td>
                                    <td className="px-4 py-4 font-bold text-gray-900">
                                        {req.type === 'Room' ? `Room ${req.roomNumber}` : req.facility}
                                    </td>
                                    <td className="px-4 py-4 max-w-xs">
                                        <div className="font-bold text-gray-900 mb-1">{req.title}</div>
                                        <div className="text-gray-500 truncate">{req.description}</div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                                            req.priority === 'Urgent' ? 'bg-rose-100 text-rose-700' :
                                            req.priority === 'High' ? 'bg-amber-100 text-amber-700' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {req.priority}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                                            req.status === 'Completed' ? 'bg-emerald-100 text-emerald-700' :
                                            req.status === 'Open' ? 'bg-indigo-100 text-indigo-700' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {req.status}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {currentLevel === 'form' ? renderForm() : renderPreview()}
        </div>
    );
};

export default MaintenanceReport;
